import React, { useState } from 'react';
import SmallDialog from '../../lib/dialog/SmallDialog';
import Button from '../../lib/button/Button';
import { getAccessToken } from '../../auth';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { envConfig } from '../../config';
import { useWorkspaceStore } from '../../store/workspace';

interface IDeleteProjectConfirmationDialogProps {
	onClose: () => void;
	projectName?: string;
}

const DeleteProjectConfirmationDialog: React.FC<
	IDeleteProjectConfirmationDialogProps
> = ({ onClose, projectName }) => {
	const [isDeletingProject, setIsDeletingProject] = useState(false);
	const { id: projectId } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);

	const deleteProject = async () => {
		setIsDeletingProject(true);

		try {
			await axios.delete(
				`${envConfig.API_BASE_URL}/editor-service/v1/project/${projectId}`,
				{
					headers: {
						Authorization: 'Bearer ' + (await getAccessToken()),
					},
				}
			);
			navigate(`/ws/${activeWorkspace?.slug}/gallery`);
		} catch (error) {
			console.error('Error deleting project:', error);
		} finally {
			setIsDeletingProject(false);
		}
	};

	return (
		<SmallDialog
			onClose={() => !isDeletingProject && onClose()}
			style={{
				display: 'flex',
				flexDirection: 'column',
				boxSizing: 'border-box',
				minHeight: '280px',
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					justifyContent: 'space-between',
					gap: '16px',
				}}
			>
				<div>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							marginBottom: '12px',
						}}
					>
						<div
							style={{
								background: '#fee2e2',
								color: '#ef4444',
								width: '36px',
								height: '36px',
								borderRadius: '10px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '18px',
								flexShrink: 0,
							}}
						>
							<i className='fa-solid fa-trash-can'></i>
						</div>
						<h2
							style={{
								fontSize: '20px',
								fontWeight: 600,
								color: '#0f172a',
								margin: 0,
							}}
						>
							Delete Project
						</h2>
					</div>

					<p
						style={{
							margin: 0,
							fontSize: '15px',
							color: '#334155',
							lineHeight: '1.5',
							marginTop: '12px',
						}}
					>
						Are you sure you want to delete{' '}
						{projectName ? (
							<strong style={{ color: '#0f172a' }}>{projectName}</strong>
						) : (
							'this project'
						)}
						?
					</p>

					<p
						style={{
							margin: 0,
							color: '#ef4444',
							fontSize: '13px',
							lineHeight: '1.5',
							marginTop: '8px',
						}}
					>
						This action is irreversible and will delete all logic and data associated with it. It will also unpublish the project.
					</p>
				</div>

				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '12px',
						marginTop: 'auto',
						paddingTop: '16px',
					}}
				>
					<Button
						variant='outlined'
						disabled={isDeletingProject}
						onClick={() => !isDeletingProject && onClose()}
					>
						Cancel
					</Button>
					<Button
						color='error'
						loading={isDeletingProject}
						onClick={deleteProject}
					>
						Delete
					</Button>
				</div>
			</div>
		</SmallDialog>
	);
};

export default DeleteProjectConfirmationDialog;
